import { ChangeEvent, FunctionComponent } from "react";
import { UseFormSetValue } from "react-hook-form";

import { useTranslation } from "react-i18next";

import { InputField } from "@/components/InputField/InputField";

import {
	CompanyEnrichAutoComplete,
	CompanyEnrichAutoCompleteItem,
} from "./CompanyEnrichAutoComplete";
import { CreateCompanyEnrich } from "../schema/companyEnrich";
import { normalizeDomainInput } from "../utils/normalizeDomainInput";

interface AdvancedSearchFiltersSectionProps {
	formValues: Partial<CreateCompanyEnrich>;
	setValue: UseFormSetValue<CreateCompanyEnrich>;
	fetchKeywords: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;

	fetchCompanyDomains: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	hideSimilarDomains?: boolean;
}

export const AdvancedSearchFiltersSection: FunctionComponent<AdvancedSearchFiltersSectionProps> = ({
	formValues,
	setValue,
	fetchCompanyDomains,
	fetchKeywords,
	hideSimilarDomains = false,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companyEnrich.${key}`);

	const hasSemanticQuery = !!formValues.companyFilterSearch?.semanticQuery?.trim();
	const hasSimilarDomains = !!formValues.companyFilterSimilar?.similarDomains?.length;

	return (
		<>
			<div className="flex flex-col gap-4 w-full mb-4">
				<CompanyEnrichAutoComplete
					fetchFn={fetchKeywords}
					handleSelect={(value) => {
						setValue("companyFilterSearch.keywords", value?.map((r) => r.id) || [], {
							shouldDirty: true,
							shouldValidate: true,
						});
					}}
					label={ts("keywordsLabel")}
					placeholder={ts("keywordsPlaceholder")}
					selectedItems={
						formValues.companyFilterSearch?.keywords?.map((r) => ({
							id: r,
							title: r,
						})) || []
					}
					testId="autocomplete-keywords"
				/>
			</div>
			<div className="flex flex-col gap-4 w-full mb-4">
				<InputField
					isDisabled={hasSimilarDomains}
					handleChange={(event: ChangeEvent<HTMLInputElement>) => {
						setValue("companyFilterSearch.semanticQuery", event.target.value || undefined, {
							shouldDirty: true,
							shouldValidate: true,
						});
					}}
					classNameWrapper="m-0!"
					label={ts("semanticQueryLabel")}
					name="semanticQuery"
					placeholder={ts("semanticQueryPlaceholder")}
					value={formValues.companyFilterSearch?.semanticQuery ?? ""}
				/>
			</div>
			{!hideSimilarDomains && (
				<div className="flex flex-col gap-4 w-full mb-4">
					<CompanyEnrichAutoComplete
						allowCustomValues
						normalizeCustomValue={normalizeDomainInput}
						disabled={hasSemanticQuery}
						fetchFn={fetchCompanyDomains}
						handleSelect={(value) => {
							setValue("companyFilterSimilar.similarDomains", value?.map((r) => r.id) || [], {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						label={ts("similarDomainsLabel")}
						placeholder={ts("similarDomainsPlaceholder")}
						selectedItems={
							formValues.companyFilterSimilar?.similarDomains?.map((r) => ({
								id: r,
								title: r,
							})) || []
						}
						testId="autocomplete-similar-domains"
					/>
				</div>
			)}
		</>
	);
};
